// Find.jsx (full replacement)
// 3-column single screen: Select Security | Select Expiration | Results Grid
// Expiration date labels are forced to UTC so the calendar date is correct.
// Adds clickable ">" (details) column to Results grid + a right-side Details Drawer.
// FIX: Saved settings (rank/strike filters + profit sort) are now applied reliably to results grid.

import React, { useCallback, useEffect, useMemo, useRef, useState, memo } from "react";
import { useNavigate } from 'react-router-dom';
import AppLayout from "./components/AppLayout";

import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";

import { get } from "@aws-amplify/api-rest";
import * as Auth from "aws-amplify/auth";

import { Button, View, withAuthenticator } from "@aws-amplify/ui-react";

import { useActiveButton } from "./ActiveButtonContext";
import { gridColumns, rowClassRules } from "./lib/grid";
import { loadRowData, downloadFile, strategy } from "./lib/common";

import "./App.css";

const myAPI = "mktappapi";

/** -----------------------------
 *  Left panel: Security input
 *  ----------------------------- */
const SecurityPanel = memo(function SecurityPanel({ onSearch, disabled }) {
  const [value, setValue] = useState("");
  const inputRef = useRef(null);

  const submit = useCallback(() => {
    const sym = (value || "").trim().toUpperCase();
    if (!sym) return;
    onSearch(sym);
  }, [onSearch, value]);

  useEffect(() => {
    inputRef.current?.focus?.();
  }, []);

  return (
    <div
      style={{
        width: 320,
        background: 'rgba(255, 255, 255, 0.95)',
        border: '1px solid rgba(229, 231, 235, 1)',
        borderRadius: '16px',
        padding: '32px',
        boxShadow: '0 10px 25px rgba(0, 0, 0, 0.08), 0 6px 12px rgba(0, 0, 0, 0.04)',
        transition: 'transform 0.2s, box-shadow 0.2s'
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'translateY(-2px)';
        e.currentTarget.style.boxShadow = '0 15px 35px rgba(0, 0, 0, 0.12), 0 8px 16px rgba(0, 0, 0, 0.06)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = '0 10px 25px rgba(0, 0, 0, 0.08), 0 6px 12px rgba(0, 0, 0, 0.04)';
      }}
    >
      <div style={{ fontSize: '18px', fontWeight: '700', marginBottom: '20px', color: 'rgba(0, 0, 0, 0.9)' }}>Select Security</div>

      <label style={{ display: "block", fontSize: '13px', fontWeight: '500', marginBottom: '8px', color: 'rgba(55, 65, 81, 1)' }}>Symbol</label>

      <input
        ref={inputRef}
        type="text"
        value={value}
        placeholder="e.g., SPY"
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") submit();
        }}
        style={{
          width: '100%',
          padding: '14px 16px',
          fontSize: '14px',
          border: '1px solid rgba(209, 213, 219, 1)',
          borderRadius: '8px',
          outline: 'none',
          transition: 'all 0.2s',
          backgroundColor: 'white',
          color: 'rgba(17, 24, 39, 1)',
          fontFamily: 'inherit',
          marginBottom: '16px'
        }}
        onFocus={(e) => {
          e.target.style.borderColor = '#3b82f6';
          e.target.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)';
        }}
        onBlur={(e) => {
          e.target.style.borderColor = 'rgba(209, 213, 219, 1)';
          e.target.style.boxShadow = 'none';
        }}
      />

      <button
        disabled={disabled || !value.trim()}
        onClick={submit}
        style={{
          width: '100%',
          padding: '14px 24px',
          background: (disabled || !value.trim()) ? 'rgba(156, 163, 175, 1)' : 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
          border: 'none',
          borderRadius: '8px',
          color: 'white',
          fontSize: '15px',
          fontWeight: '600',
          cursor: (disabled || !value.trim()) ? 'not-allowed' : 'pointer',
          transition: 'all 0.2s',
          boxShadow: (disabled || !value.trim()) ? 'none' : '0 4px 6px rgba(59, 130, 246, 0.3)',
        }}
        onMouseEnter={(e) => {
          if (!disabled && value.trim()) {
            e.currentTarget.style.transform = 'translateY(-1px)';
            e.currentTarget.style.boxShadow = '0 6px 12px rgba(59, 130, 246, 0.4)';
          }
        }}
        onMouseLeave={(e) => {
          if (!disabled && value.trim()) {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 4px 6px rgba(59, 130, 246, 0.3)';
          }
        }}
      >
        Find Expirations
      </button>

      <div style={{ marginTop: '12px', fontSize: '12px', color: 'rgba(107, 114, 128, 1)' }}>
        Tip: press <b>Enter</b> to search.
      </div>
    </div>
  );
});

/** -----------------------------
 *  Middle panel: Expirations grid
 *  ----------------------------- */
const ExpirationPanel = memo(function ExpirationPanel({
  symbol,
  rowData,
  loading,
  error,
  onSelectExpiration,
}) {
  const expApiRef = useRef(null);

  const columns = useMemo(
    () => [
      {
        headerName: "Expiration",
        field: "expirations",
        sortable: true,
        filter: true,
        resizable: true,
        flex: 1,
        minWidth: 220,
      },
    ],
    []
  );

  return (
    <div
      style={{
        width: 360,
        background: 'rgba(255, 255, 255, 0.95)',
        border: '1px solid rgba(229, 231, 235, 1)',
        borderRadius: '16px',
        padding: '32px',
        boxShadow: '0 10px 25px rgba(0, 0, 0, 0.08), 0 6px 12px rgba(0, 0, 0, 0.04)',
        transition: 'transform 0.2s, box-shadow 0.2s'
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'translateY(-2px)';
        e.currentTarget.style.boxShadow = '0 15px 35px rgba(0, 0, 0, 0.12), 0 8px 16px rgba(0, 0, 0, 0.06)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = '0 10px 25px rgba(0, 0, 0, 0.08), 0 6px 12px rgba(0, 0, 0, 0.04)';
      }}
    >
      <div style={{ fontSize: '18px', fontWeight: '700', marginBottom: '20px', color: 'rgba(0, 0, 0, 0.9)' }}>
        Select Expiration{symbol ? ` — ${symbol}` : ""}
      </div>

      {error ? (
        <div style={{ color: "#b91c1c", marginBottom: 8, fontSize: 13 }}>{error}</div>
      ) : null}

      <div
        className="ag-theme-quartz"
        style={{
          height: 540,
          width: "100%",
          opacity: loading ? 0.6 : 1,
          pointerEvents: loading ? "none" : "auto",
        }}
      >
        <AgGridReact
          rowData={rowData}
          columnDefs={columns}
          rowSelection="single"
          overlayLoadingTemplate={'<span class="ag-overlay-loading-center">Loading expirations...</span>'}
          overlayNoRowsTemplate={'<span class="ag-overlay-loading-center">No expirations. Search a symbol.</span>'}
          onGridReady={(params) => {
            expApiRef.current = params.api;
            params.api.showNoRowsOverlay();
          }}
          onSelectionChanged={() => {
            if (loading) return;
            const selected = expApiRef.current?.getSelectedRows?.() || [];
            if (!selected.length) return;
            onSelectExpiration(selected[0]);
          }}
        />
      </div>

      <div style={{ marginTop: '12px', fontSize: '12px', color: 'rgba(107, 114, 128, 1)' }}>
        The list is disabled while loading.
      </div>
    </div>
  );
});

/** -----------------------------
 *  Right panel: Results grid
 *  ----------------------------- */
const ResultsPanel = memo(function ResultsPanel({
  columns,
  rowData,
  loading,
  message,
  activeButton,
  onGridReady,
  onFirstDataRendered,
  onCellClicked,
}) {
  return (
    <div
      style={{
        flex: 1,
        minWidth: 520,
        background: "#fff",
        border: "1px solid #e5e7eb",
        borderRadius: 10,
        padding: 12,
      }}
    >
      <div style={{ fontWeight: 700, marginBottom: 8 }}>Results</div>

      {loading ? (
        <div className="blink_me" style={{ marginBottom: 8 }}>
          {message || "Loading..."}
        </div>
      ) : message ? (
        <div style={{ marginBottom: 8, fontSize: 13, color: "#6b7280" }}>{message}</div>
      ) : null}

      <div className="ag-theme-quartz" style={{ height: 600, width: "100%" }}>
        <AgGridReact
          rowData={rowData}
          columnDefs={columns}
          rowSelection="multiple"
          suppressRowClickSelection={true}
          rowClassRules={activeButton === "inTheMoney" ? rowClassRules : null}
          onGridReady={onGridReady}
          onFirstDataRendered={onFirstDataRendered}
          onCellClicked={onCellClicked}
        />
      </div>
    </div>
  );
});

/** -----------------------------
 *  Details Drawer (right side)
 *  ----------------------------- */
function DetailsDrawer({ row, onClose }) {
  if (!row) return null;

  const excluded = new Set(["id", "details", "expanded"]);
  const entries = Object.entries(row).filter(([k]) => !excluded.has(k));

  const pairs = [];
  for (let i = 0; i < entries.length; i += 2) pairs.push(entries.slice(i, i + 2));

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        right: 0,
        height: "100vh",
        width: 420,
        background: "#FFFFFF",
        borderLeft: "1px solid #E5E7EB",
        boxShadow: "0 10px 25px rgba(0,0,0,0.12)",
        zIndex: 9999,
        padding: 16,
        overflowY: "auto",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div style={{ fontWeight: 700, fontSize: 16, color: "#111827" }}>Details</div>
        <button
          onClick={onClose}
          style={{
            border: "1px solid #E5E7EB",
            background: "#F9FAFB",
            borderRadius: 8,
            padding: "6px 10px",
            cursor: "pointer",
          }}
        >
          Close
        </button>
      </div>

      <div style={{ marginTop: 12 }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <tbody>
            {pairs.map((pair, idx) => (
              <tr key={idx}>
                {pair.map(([k, v]) => (
                  <React.Fragment key={k}>
                    <td
                      style={{
                        width: "30%",
                        padding: 8,
                        fontWeight: 600,
                        fontSize: 12,
                        color: "#374151",
                        background: "#F3F4F6",
                        border: "1px solid #E5E7EB",
                        verticalAlign: "top",
                      }}
                    >
                      {k}
                    </td>
                    <td
                      style={{
                        width: "20%",
                        padding: 8,
                        fontSize: 12,
                        color: "#111827",
                        border: "1px solid #E5E7EB",
                        verticalAlign: "top",
                        wordBreak: "break-word",
                      }}
                    >
                      {String(v)}
                    </td>
                  </React.Fragment>
                ))}
                {pair.length === 1 ? (
                  <>
                    <td style={{ border: "1px solid #E5E7EB", background: "#F3F4F6" }} />
                    <td style={{ border: "1px solid #E5E7EB" }} />
                  </>
                ) : null}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/** -----------------------------
 *  Main component
 *  ----------------------------- */
const Find = ({ signOut }) => {
  const { activeButton } = useActiveButton();
  const navigate = useNavigate();

  // --------- refs ----------
  const today = Math.floor(Date.now() / 1000);
  const email = useRef("");
  const data = useRef({});
  const gridData = useRef(null);
  const symbolsDetails = useRef([]);

  const gridApi = useRef(null);
  const gridColumnApi = useRef(null);

  // Used to ensure saved settings get applied after columns exist
  const savedAppliedKeyRef = useRef(""); // track activeButton+columns readiness

  // --------- state ----------
  const [initLoaded, setInitLoaded] = useState(false);

  // Search / expiration state
  const [searchedSymbol, setSearchedSymbol] = useState("");
  const [expRowData, setExpRowData] = useState([]);
  const [expLoading, setExpLoading] = useState(false);
  const [expError, setExpError] = useState("");

  // Results
  const [rowDataState, setRowDataState] = useState([]);
  const [analysisLoading, setAnalysisLoading] = useState(false);
  const [analysisMessage, setAnalysisMessage] = useState(
    "Select a symbol and expiration to run analysis."
  );

  // Details drawer
  const [detailsRow, setDetailsRow] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const closeDetails = useCallback(() => {
    setShowDetails(false);
    setDetailsRow(null);
  }, []);

  // ---------- bootstrap ----------
  useEffect(() => {
    (async () => {
      try {
        await Auth.fetchAuthSession();
        const user = await Auth.fetchUserAttributes();
        email.current = user.email || "";

        // symbols details
        const respSymbols = await get({ apiName: myAPI, path: "/getSymbolsDetails" }).response;
        const symbols = await respSymbols.body.json();
        symbolsDetails.current = Array.isArray(symbols) ? symbols : [];

        // saved settings
        const raw = await downloadFile(email.current);
        let parsed = null;
        try {
          parsed = raw ? JSON.parse(raw) : null;
        } catch {
          parsed = null;
        }
        if (!parsed || typeof parsed !== "object") {
          parsed = {
            rank: "ALL",
            strikeMin: "",
            strikeMax: "",
            profitSort: "",
            days: "30",
            daysOffset: "10",
            minInterest: "0.7",
            maxInterest: "1.5",
          };
        }

        gridData.current = parsed;
        data.current = parsed;

        setInitLoaded(true);
      } catch (e) {
        setAnalysisMessage(e?.message || "Failed to initialize.");
        setInitLoaded(true);
      }
    })();
  }, []);

  // ---------- DETAILS column (">") ----------
  const detailsColDef = useMemo(
    () => ({
      headerName: "",
      field: "details",
      width: 46,
      pinned: "left",
      sortable: false,
      filter: false,
      resizable: false,
      suppressMenu: true,
      cellRenderer: () => (
        <span
          style={{
            display: "inline-flex",
            width: "100%",
            justifyContent: "center",
            fontSize: 18,
            color: "#22C55E",
            cursor: "pointer",
            userSelect: "none",
            lineHeight: "20px",
          }}
          title="View details"
        >
          ›
        </span>
      ),
    }),
    []
  );

  // ---------- result columns ----------
  const columns = useMemo(() => {
    if (!initLoaded) return [];
    if (!symbolsDetails.current || symbolsDetails.current.length === 0) return [];

    const base = gridColumns(
      symbolsDetails.current,
      symbolsDetails.current,
      0,
      activeButton,
      data.current || {}
    );

    return [detailsColDef, ...(Array.isArray(base) ? base : [])];
  }, [activeButton, initLoaded, detailsColDef]);

  // ---------- apply saved filters/sorts RELIABLY ----------
  const applySavedGridState = useCallback((api) => {
    const saved = gridData.current;
    if (!api || !saved) return;

    // Build filter model without needing filter instances
    const filterModel = {};

    // rank filter (ranking)
    if (saved.rank && saved.rank !== "ALL") {
      filterModel.ranking = {
        filterType: "number",
        type: "equals",
        filter: Number(saved.rank),
      };
    }

    // strike filter (strike)
    const hasMin = saved.strikeMin !== "" && saved.strikeMin != null;
    const hasMax = saved.strikeMax !== "" && saved.strikeMax != null;

    if (hasMin || hasMax) {
      if (hasMin && !hasMax) {
        filterModel.strike = {
          filterType: "number",
          type: "greaterThanOrEqual",
          filter: Number(saved.strikeMin),
        };
      } else if (!hasMin && hasMax) {
        filterModel.strike = {
          filterType: "number",
          type: "lessThanOrEqual",
          filter: Number(saved.strikeMax),
        };
      } else {
        filterModel.strike = {
          filterType: "number",
          operator: "AND",
          condition1: {
            filterType: "number",
            type: "greaterThanOrEqual",
            filter: Number(saved.strikeMin),
          },
          condition2: {
            filterType: "number",
            type: "lessThanOrEqual",
            filter: Number(saved.strikeMax),
          },
        };
      }
    }

    // Apply filters
    api.setFilterModel(filterModel);
    api.onFilterChanged();

    // profit sort
    if (saved.profitSort) {
      const sortDirection = saved.profitSort === "DESC" ? "desc" : "asc";
      api.applyColumnState({
        state: [{ colId: "profit", sort: sortDirection }],
        applyOrder: true,
      });
    }
  }, []);

  // Apply saved settings once grid+columns are truly ready (and re-apply on activeButton change)
  useEffect(() => {
    const api = gridApi.current;
    if (!initLoaded || !api) return;
    if (!columns || columns.length === 0) return;

    const key = `${activeButton}|cols:${columns.length}`;
    if (savedAppliedKeyRef.current === key) return;

    // Next tick ensures AG Grid has processed the latest columnDefs
    const t = setTimeout(() => {
      applySavedGridState(api);
      savedAppliedKeyRef.current = key;
    }, 0);

    return () => clearTimeout(t);
  }, [initLoaded, columns, activeButton, applySavedGridState]);

  // ---------- expiration fetch ----------
  const fetchExpirations = useCallback(
    async (symbol) => {
      setSearchedSymbol(symbol);
      setExpError("");
      setExpRowData([]);
      setRowDataState([]);
      setAnalysisMessage("Select an expiration to run analysis.");
      setAnalysisLoading(false);
      closeDetails();

      setExpLoading(true);
      try {
        const resp = await get({ apiName: myAPI, path: `/expirations/${symbol}` }).response;
        const json = await resp.body.json();

        const rows = (json?.expDates || []).map((unix, idx) => {
          const d = new Date(Number(unix) * 1000);

          // Force UTC so the calendar date stays correct across timezones
          const label = new Intl.DateTimeFormat(undefined, {
            weekday: "short",
            year: "numeric",
            month: "short",
            day: "2-digit",
            timeZone: "UTC",
          }).format(d);

          return {
            id: idx,
            expirationsUnix: Number(unix),
            expirations: label,
          };
        });

        setExpRowData(rows);
        if (!rows.length) setExpError("No expirations returned for this symbol.");
      } catch (e) {
        setExpError(e?.message || "Failed to fetch expirations.");
      } finally {
        setExpLoading(false);
      }
    },
    [closeDetails]
  );

  // ---------- run strategy when expiration is selected ----------
  const runAnalysisForExpiration = useCallback(
    async (expRow) => {
      if (!initLoaded) return;
      if (!searchedSymbol) return;

      await console.log("runAnalysisForExpiration : expRow = ", expRow);
      await console.log("runAnalysisForExpiration : expRow.expirationsUnix = ", expRow.expirationsUnix);
      await console.log("runAnalysisForExpiration : today = ", today);

      const unix = expRow.expirationsUnix;
      const daysNowToExp = (unix - today) / 60 / 60 / 24;

      await console.log("runAnalysisForExpiration : daysNowToExp = ", daysNowToExp);

      setAnalysisLoading(true);
      setAnalysisMessage("Loading analysis...");
      closeDetails();

      try {
        const params = {
          ...(data.current || {}),
          stocks: searchedSymbol,
        };

        const daysStart = Math.max(0, Math.ceil(daysNowToExp)); //Math.max(0, Math.floor(daysNowToExp));
        const daysEnd = Math.max(0, Math.floor(daysNowToExp + 2));

        params.days = String(daysStart);
        params.daysOffset = String(daysEnd);
        params.minDepth = "0";
        params.useThirdFriday = undefined;
        params.startingFromThisMonth = undefined;

        const strategyAnalysis = await strategy(JSON.stringify(params), activeButton);
        const rowdata = await loadRowData(strategyAnalysis, daysNowToExp, activeButton);

        setRowDataState(Array.isArray(rowdata) ? rowdata : []);
        setAnalysisMessage(Array.isArray(rowdata) && rowdata.length ? "" : "No rows returned.");
      } catch (e) {
        setRowDataState([]);
        setAnalysisMessage(e?.message || "Failed to load analysis.");
      } finally {
        setAnalysisLoading(false);
      }
    },
    [activeButton, initLoaded, searchedSymbol, today, closeDetails]
  );

  // ---------- grid callbacks ----------
  const onResultsGridReady = useCallback((params) => {
    gridApi.current = params.api;
    gridColumnApi.current = params.columnApi;
    // Do NOT apply saved state here (columns may not be ready yet).
    // The useEffect above applies it after columns exist.
  }, []);

  const onFirstDataRendered = useCallback((params) => {
    const all = [];
    params.api.getColumns().forEach((c) => all.push(c.colId));
    params.api.autoSizeColumns(all);
  }, []);

  // ---------- DETAILS click handler ----------
  const onResultsCellClicked = useCallback((event) => {
    if (!event?.colDef) return;
    if (event.colDef.field === "details") {
      // Navigate to stock detail page instead of showing drawer
      console.log("Navigating to stock detail page with data:", event.data);
      navigate('/stockDetail', { state: { stockData: event.data } });
    }
  }, [navigate]);

  return (
    <AppLayout signOut={signOut}>
      <View className="AppFullScreen">
        <h1 style={{ fontSize: '28px', fontWeight: '700', color: 'rgba(0, 0, 0, 0.9)', marginBottom: '8px' }}>
          B/W - Analysis For Security / Expiration
        </h1>

        <div
          style={{
            display: "flex",
            gap: 16,
            alignItems: "flex-start",
            marginTop: 12,
            width: "100%",
          }}
        >
          {/* 1) Select Security */}
          <SecurityPanel onSearch={fetchExpirations} disabled={!initLoaded || expLoading} />

          {/* 2) Select Expiration */}
          <ExpirationPanel
            symbol={searchedSymbol}
            rowData={expRowData}
            loading={expLoading}
            error={expError}
            onSelectExpiration={runAnalysisForExpiration}
          />

          {/* 3) Results */}
          <ResultsPanel
            columns={columns}
            rowData={rowDataState}
            loading={analysisLoading}
            message={analysisMessage}
            activeButton={activeButton}
            onGridReady={onResultsGridReady}
            onFirstDataRendered={onFirstDataRendered}
            onCellClicked={onResultsCellClicked}
          />
        </div>

        {showDetails && <DetailsDrawer row={detailsRow} onClose={() => { setShowDetails(false); setDetailsRow(null); }} />}
      </View>
    </AppLayout>
  );
};

export default withAuthenticator(Find);